import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    Typography,
    TextField,
    IconButton,
    Box,
    Paper,
    CircularProgress,
    Alert,
    List,
    ListItem,
    ListItemButton,
    Divider,
    Drawer,
    Chip,
    Fab,
    Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import ChatIcon from '@mui/icons-material/Chat';
import RemoveIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatSettingsButton from './ChatSettingsButton';
import useChatLlmProvider from '../../hooks/useChatLlmProvider';
import Citations from './Citations';
import ChatMessageMeta from './ChatMessageMeta';
import MessageActionsRow from './MessageActionsRow';
import TypingIndicator from './TypingIndicator';
import { OPEN_PROMPTA_CHAT } from './promptaChat';
import useStore from '../../store';
import insightsApi from '../../api/insightsApi';
import { token_keys } from '../../constants';

// Base URL for backend
const BACKEND_BASE_URL =
    process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === 'development'
        ? 'http://localhost:8000'
        : null);

if (!BACKEND_BASE_URL) {
    console.error('BACKEND_BASE_URL is missing');
}

// Convert http(s) URL to ws(s) URL for WebSocket connection
const getWebSocketUrl = (httpUrl) => {
    if (httpUrl.startsWith('https://')) {
        return httpUrl.replace('https://', 'wss://');
    }
    if (httpUrl.startsWith('http://')) {
        return httpUrl.replace('http://', 'ws://');
    }
    return httpUrl;
};

// localStorage key for the panel's horizontal drag position
const POSITION_X_STORAGE_KEY = 'chatbot.position.x';

const Chatbot = ({ reportId }) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [drawerHeight, setDrawerHeight] = useState(480);
    const [drawerWidth, setDrawerWidth] = useState(null); // null = use default responsive width
    // Position: positionX = horizontal translate offset in px from the
    // default right-docked spot (0 = docked right, negative = towards the
    // left edge). Draggable via the header; posY 0=bottom, 1=top.
    const [positionX, setPositionX] = useState(() => {
        const stored = parseFloat(localStorage.getItem(POSITION_X_STORAGE_KEY));
        return Number.isFinite(stored) ? stored : 0;
    });
    const [isDragging, setIsDragging] = useState(false);
    const [posY, setPosY] = useState(0);
    const panelRef = useRef(null);
    const dragStartRef = useRef({ mouseX: 0, posX: 0 });
    const didDragRef = useRef(false);
    const [userInput, setUserInput] = useState('');
    const [llmProvider, setLlmProvider] = useChatLlmProvider();
    // const [messages, setMessages] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // What the BE agent is looking up right now ({ tool, label }), for the loading state
    const [agentStatus, setAgentStatus] = useState(null);
    const [error, setError] = useState(null);
    const [isConnected] = useState(true);

    const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
    const [chatSessions, setChatSessions] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);

    const messagesEndRef = useRef(null);
    const websocketRef = useRef(null);

    const language = useStore((state) => state.language);
    // const [qaHistory, setQaHistory] = useState([]);
    // ---- Ask-Prompta store wiring ----
    const channel = 'prompta_insights';
    const askState = useStore((state) => state.askPrompta[`${reportId}::${channel}`]);
    const initSession = useStore((state) => state.initAskPromptaSession);
    const appendUserMessage = useStore((state) => state.appendUserMessage);
    const appendAssistantChunk = useStore((state) => state.appendAssistantChunk);
    const attachAssistantDocuments = useStore((state) => state.attachAssistantDocuments);
    const finalizeAssistantMessage = useStore((state) => state.finalizeAssistantMessage);
    const loadHistorySession = useStore((state) => state.loadHistorySession);
    const ensureMessageId = useStore((state) => state.ensureMessageId);
    const setMessageFeedback = useStore((state) => state.setMessageFeedback);
    const messages = askState?.messages || [];
    const qaHistory = askState?.qaHistory || [];
    const sessionId = askState?.sessionId;
    const isViewingHistory = askState?.isViewingHistory;
    const clearAskPromptaMessages = useStore(
        (state) => state.clearAskPromptaMessages
    );

    // ---- horizontal drag positioning ----

    // Keep at least 20% of the panel visible on either edge
    const clampPositionX = useCallback((x) => {
        const width = panelRef.current?.offsetWidth || 480;
        const defaultLeft = window.innerWidth - 16 - width; // untranslated left edge (right: 16)
        const minX = -0.8 * width - defaultLeft;
        const maxX = window.innerWidth - 0.2 * width - defaultLeft;
        return Math.min(Math.max(x, minX), maxX);
    }, []);

    // translateX for the left-docked position (mirror of right: 16)
    const dockLeftX = useCallback(() => {
        const width = panelRef.current?.offsetWidth || 480;
        return 32 + width - window.innerWidth;
    }, []);

    const updatePositionX = useCallback(
        (x) => setPositionX(clampPositionX(x)),
        [clampPositionX]
    );

    // openPromptaChat(question) from elsewhere on the page (the By-group chat's
    // redirect): open the panel with the question ready to send.
    useEffect(() => {
        const onOpen = (e) => {
            setIsOpen(true);
            const question = e.detail?.question;
            if (question) setUserInput(question);
        };
        window.addEventListener(OPEN_PROMPTA_CHAT, onOpen);
        return () => window.removeEventListener(OPEN_PROMPTA_CHAT, onOpen);
    }, []);

    // persist position; restore happens in the useState initializer
    useEffect(() => {
        localStorage.setItem(POSITION_X_STORAGE_KEY, String(positionX));
    }, [positionX]);

    // re-clamp on mount (restored value may be stale) and on window resize
    useEffect(() => {
        const onResize = () => setPositionX((x) => clampPositionX(x));
        onResize();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [clampPositionX]);

    const handleDragStart = (clientX, target) => {
        // don't start a drag from the header's icon buttons / chip
        if (target.closest('button, .MuiChip-root')) return;
        dragStartRef.current = { mouseX: clientX, posX: positionX };
        didDragRef.current = false;
        setIsDragging(true);
    };

    useEffect(() => {
        if (!isDragging) return undefined;

        const moveTo = (clientX) => {
            const delta = clientX - dragStartRef.current.mouseX;
            if (Math.abs(delta) > 3) didDragRef.current = true;
            updatePositionX(dragStartRef.current.posX + delta);
        };
        const onMouseMove = (e) => moveTo(e.clientX);
        const onTouchMove = (e) => {
            if (e.touches.length > 0) moveTo(e.touches[0].clientX);
        };
        const endDrag = () => setIsDragging(false);

        // listen on document so the drag survives leaving the header
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', endDrag);
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', endDrag);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', endDrag);
            document.removeEventListener('touchmove', onTouchMove);
            document.removeEventListener('touchend', endDrag);
        };
    }, [isDragging, updatePositionX]);

    const scrollToBottom = () => {
        // block: 'nearest' scrolls the minimum needed — 'end' over-scrolled
        // and clipped the typing indicator below the viewport edge
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    // isLoading must be a trigger too: the typing indicator mounts on its
    // own when isLoading flips, without a corresponding messages change
    useEffect(() => {
        scrollToBottom();
    }, [messages, isLoading]);

    useEffect(() => {
        if (isViewingHistory && messages.length > 0) {
            scrollToBottom();
        }
    }, [isViewingHistory, messages.length]);

    // init new session on mount / refresh
    useEffect(() => {
        if (reportId && !askState) {
            initSession(reportId, channel);
        }
        return () => {
            clearAskPromptaMessages(reportId, channel);
            websocketRef.current?.close();
        };
    }, [reportId]);
    
    const buildHistoryForRAG = () => {
        return qaHistory.slice(-5).map((item) => ({
            question: item.question,
            answer: item.answer,
        }));
    };

    const startWebSocketStream = async (question) => {
        // Close existing WebSocket connection if any
        if (websocketRef.current) {
            websocketRef.current.close();
        }

        // Keep history computation for future use (BE currently only needs user_input and language)
        const accessToken = localStorage.getItem(token_keys.access_token);
        const historyForRAG = buildHistoryForRAG();
        // eslint-disable-next-line no-unused-vars
        const wsBaseUrl = getWebSocketUrl(BACKEND_BASE_URL);

        let documentIds = [];
        try {
            const data = await insightsApi.getDocuments(reportId);
            const docs = data.results || [];
            documentIds = docs.map(doc => doc.id);
        } catch (err) {
            console.error("Failed to fetch linked documents for WebSocket:", err);
        }
        const url = `${wsBaseUrl}/ws/rag/`;

        const ws = new WebSocket(url, ["Bearer", accessToken]);
        websocketRef.current = ws;

        setIsLoading(true);
        setAgentStatus(null);
        
        ws.onopen = () => {
            console.log('WebSocket connected');
            // Send the question to the server
            ws.send(JSON.stringify({
                user_input: question,
                history: historyForRAG,
                channel: 'prompta_insights',
                session_id: sessionId,// Uncomment when BE supports it
                document_ids: documentIds,
                llm_provider: llmProvider,
                report_ids: [reportId],
            }));
            console.log("document ids sent over websocket:", documentIds);
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('received data: ', data);

                // Capture RAG citation documents whenever a frame carries them
                const retrievalDocs = data.retrieval?.documents;
                if (Array.isArray(retrievalDocs) && retrievalDocs.length > 0) {
                    attachAssistantDocuments(reportId, channel, retrievalDocs);
                }

                // The agent's current lookup: shown in the loading state, never in the text
                if ('status' in data) {
                    setAgentStatus(data.status);
                    return;
                }

                if (data.done) {
                    setAgentStatus(null);
                    finalizeAssistantMessage(reportId, channel);
                    ws.close();
                    setIsLoading(false);
                    // Stop here: a done frame may also carry a message field;
                    // appending it after finalize would push a new
                    // isStreaming message that nothing ever finalizes, which
                    // permanently suppresses the typing indicator
                    // (`!messages.some(m => m.isStreaming)`).
                    return;
                }

                if (!data.message) {
                    return;
                }
                // Parse "data:  token1\n\ndata:  token2\n\n" format
                const tokens = data.message
                    .split('\n\n')
                    .map(part => part.replace('data: ', ''))
                    .filter(part => part.length > 0);
                const concatenatedMessages = tokens.join('').replaceAll('$n$l$', '\n');
                setAgentStatus(null);
                appendAssistantChunk(reportId, channel, concatenatedMessages);
                console.log('concatenatedMessages: ', concatenatedMessages);

                if (tokens.length > 0) {
                    console.log('chunk: ', concatenatedMessages);
                }
            } catch (err) {
                console.error('WebSocket parse error:', err);
            }
        };

        ws.onerror = (error) => {
            console.error('WebSocket error:', error);
            setError(intl.formatMessage({ id: 'askPrompta.connection_error', defaultMessage: 'Connection error.' }));
            setIsLoading(false);
        };

        ws.onclose = (event) => {
            console.log('WebSocket closed:', event.code, event.reason);
            if (!event.wasClean && isLoading) {
                setError(intl.formatMessage({ id: 'askPrompta.connection_lost', defaultMessage: 'Connection lost.' }));
                setIsLoading(false);
            }
        };
    };

    const loadChatHistory = async () => {
        try {
            setLoadingHistory(true);
            const sessions = await insightsApi.getChatHistorySessions(reportId, channel);
            setChatSessions(sessions);
        } catch (err) {
            setError(intl.formatMessage({ id: 'askPrompta.load_history_failed', defaultMessage: 'Failed to load chat history' }));
        } finally {
            setLoadingHistory(false);
        }
    };

    const loadSessionMessages = async (sessionId) => {
        try {
            websocketRef.current?.close();
            setIsLoading(true);
            setAgentStatus(null);
            const rows = await insightsApi.getChatSessionMessages(sessionId, channel);
            const msgs = [];
            const history = [];

            rows.forEach(r => {
                msgs.push({ role: 'user', content: r.question });
                msgs.push({ role: 'assistant', content: r.answer });
                history.push({ question: r.question, answer: r.answer });
            });

            loadHistorySession(reportId, channel, sessionId, msgs, history);
            setSelectedSessionId(sessionId);
            setHistoryDrawerOpen(false);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (reportId && isOpen) {
            loadChatHistory();
        }

        return () => websocketRef.current?.close();
    }, [reportId, isOpen]);

    useEffect(() => {
        if (historyDrawerOpen && reportId) {
            loadChatHistory();
        }
    }, [historyDrawerOpen, reportId]);

    const handleSend = () => {
        if (!userInput.trim() || isLoading) return;

        const userMessage = userInput.trim();
        appendUserMessage(reportId, channel, userMessage);
        setUserInput('');
        setIsLoading(true);
        setAgentStatus(null);
        startWebSocketStream(userMessage);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Re-send the closest user question preceding the given message index
    const handleRegenerate = (index) => {
        if (isLoading) return;
        for (let i = index - 1; i >= 0; i--) {
            if (messages[i].role === 'user' && messages[i].content) {
                const question = messages[i].content;
                appendUserMessage(reportId, channel, question);
                setIsLoading(true);
                setAgentStatus(null);
                startWebSocketStream(question);
                return;
            }
        }
    };

    const handleFeedback = (index, message, vote) => {
        // re-voting the same way is a no-op — clearing a vote is not
        // supported by the feedback endpoint yet
        if (message.feedback === vote) return;
        const messageId = message.messageId || ensureMessageId(reportId, channel, index);
        setMessageFeedback(reportId, channel, messageId, vote);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return intl.formatMessage({ id: 'askPrompta.today', defaultMessage: 'Today' });
        if (diffDays === 1) return intl.formatMessage({ id: 'askPrompta.yesterday', defaultMessage: 'Yesterday' });
        if (diffDays < 7) return intl.formatMessage({ id: 'askPrompta.days_ago', defaultMessage: '{count} days ago' }, { count: diffDays });
        return date.toLocaleDateString();
    };

    const renderText = (text) => {
        if (!text) return null;
        // remarkGfm parses the tables/task-lists answers routinely come back as;
        // without it a table renders as a paragraph of pipe characters
        return <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>;
    };

    const toggleExpand = () => {
        if (isExpanded) {
            setDrawerHeight(480);
            setDrawerWidth(null);
        } else {
            setDrawerHeight(window.innerHeight - 100);
            setDrawerWidth(Math.min(1000, window.innerWidth - 32));
        }
        setIsExpanded(!isExpanded);
    };

    // Preset height options for quick selection
    const cycleHeight = () => {
        const heights = [360, 480, 600, 720];
        const currentIndex = heights.findIndex(h => Math.abs(h - drawerHeight) < 50);
        const nextIndex = (currentIndex + 1) % heights.length;
        setDrawerHeight(heights[nextIndex]);
        setIsExpanded(false);
        setDrawerWidth(null);
    };

    return (
        <>
            {/* Floating Action Button */}
            <Tooltip title={intl.formatMessage({ id: 'chatbot.prompta_insights', defaultMessage: 'Prompta Insights' })} placement="left">
                <Fab
                    color="primary"
                    onClick={() => setIsOpen(true)}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        zIndex: 1000,
                        display: isOpen ? 'none' : 'flex',
                        background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                        boxShadow: '0 4px 20px rgba(25, 118, 210, 0.4)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                            transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    <AutoAwesomeIcon />
                </Fab>
            </Tooltip>

            {/* Sliding Chat Window - X position is a free-form px offset (header-draggable), posY (0=bottom, 1=top). Animated via transform. */}
            <Paper
                ref={panelRef}
                elevation={8}
                sx={{
                    position: 'fixed',
                    right: 16,
                    bottom: 16,
                    width: drawerWidth != null ? drawerWidth : { xs: 'calc(100% - 32px)', sm: 504, md: 576 },
                    height: isOpen ? drawerHeight : 0,
                    bgcolor: 'background.paper',
                    transition: isDragging
                        ? 'none'
                        : 'height 0.3s ease, width 0.3s ease, transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    transform: `translate(
                        ${positionX}px,
                        ${posY === 1 ? 'calc(-100vh + var(--chat-height, 400px) + 32px)' : '0'}
                    )`,
                    '--chat-height': `${drawerHeight}px`,
                    zIndex: 1200,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderRadius: 3,
                    opacity: isOpen ? 1 : 0,
                    pointerEvents: isOpen ? 'auto' : 'none',
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        px: 2,
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'primary.main',
                        color: 'white',
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                        cursor: isDragging ? 'grabbing' : 'grab',
                        userSelect: 'none',
                        touchAction: 'none',
                    }}
                    onMouseDown={(e) => handleDragStart(e.clientX, e.target)}
                    onTouchStart={(e) => {
                        if (e.touches.length > 0) handleDragStart(e.touches[0].clientX, e.target);
                    }}
                    onClick={() => {
                        // a completed drag shouldn't also cycle the height
                        if (didDragRef.current) {
                            didDragRef.current = false;
                            return;
                        }
                        cycleHeight();
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AutoAwesomeIcon fontSize="small" />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            <FormattedMessage id="chatbot.prompta_insights" defaultMessage="Prompta Insights" />
                        </Typography>
                        {isViewingHistory && (
                            <Chip
                                label={intl.formatMessage({ id: 'askPrompta.history', defaultMessage: 'History' })}
                                size="small"
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    color: 'white',
                                    height: 20,
                                    '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
                                    '& .MuiChip-deleteIcon': {
                                        color: 'rgba(255,255,255,0.7)',
                                        fontSize: 14,
                                        '&:hover': { color: 'white' },
                                    },
                                }}
                                onDelete={(e) => {
                                    e.stopPropagation();
                                    setSelectedSessionId(null);
                                    initSession(reportId, channel);
                                }}
                            />
                        )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                        <Tooltip title={positionX > dockLeftX() / 2 ? intl.formatMessage({ id: 'chatbot.move_left', defaultMessage: 'Move left' }) : intl.formatMessage({ id: 'chatbot.move_right', defaultMessage: 'Move right' })}>
                            <IconButton
                                size="small"
                                onClick={() => updatePositionX(positionX > dockLeftX() / 2 ? dockLeftX() : 0)}
                                sx={{ color: 'white' }}
                            >
                                {positionX > dockLeftX() / 2 ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={posY === 0 ? intl.formatMessage({ id: 'chatbot.move_up', defaultMessage: 'Move up' }) : intl.formatMessage({ id: 'chatbot.move_down', defaultMessage: 'Move down' })}>
                            <IconButton
                                size="small"
                                onClick={() => setPosY((y) => 1 - y)}
                                sx={{ color: 'white' }}
                            >
                                {posY === 0 ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={intl.formatMessage({ id: 'askPrompta.history', defaultMessage: 'History' })}>
                            <IconButton
                                size="small"
                                onClick={() => setHistoryDrawerOpen(true)}
                                sx={{ color: 'white' }}
                            >
                                <HistoryIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={isExpanded ? intl.formatMessage({ id: 'chatbot.collapse', defaultMessage: 'Collapse' }) : intl.formatMessage({ id: 'chatbot.expand', defaultMessage: 'Expand' })}>
                            <IconButton
                                size="small"
                                onClick={toggleExpand}
                                sx={{ color: 'white' }}
                            >
                                {isExpanded ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={intl.formatMessage({ id: 'chatbot.close', defaultMessage: 'Close' })}>
                            <IconButton
                                size="small"
                                onClick={() => setIsOpen(false)}
                                sx={{ color: 'white' }}
                            >
                                <RemoveIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Chat Content */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 1.5 }}>
                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 1, py: 0 }}
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Messages Container */}
                    <Paper
                        elevation={0}
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            mb: 1.5,
                            p: 1.5,
                            bgcolor: '#f8fafc',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                        }}
                    >
                        {messages.length === 0 && !isViewingHistory && (
                            <Box sx={{ textAlign: 'center', mt: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <FormattedMessage id="askPrompta.ask_placeholder" defaultMessage="Ask me anything about this report..." />
                                </Typography>
                            </Box>
                        )}

                        {messages.map((message, index) => (
                            // Render guard: an assistant message with no
                            // content and no active stream would paint an
                            // empty white bubble — skip it entirely.
                            message.role === 'assistant' && !message.content && !message.isStreaming ? null : (
                            <Box key={index} sx={{ mb: 1.5 }}>
                                <ChatMessageMeta message={message} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                                    }}
                                >
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: 1.5,
                                            maxWidth: '85%',
                                            bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                                            color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                            borderRadius: message.role === 'user' ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                                            // GFM tables render borderless by default — give
                                            // assistant answers the same bordered styling as
                                            // AskPrompta, and let a wide table scroll inside
                                            // the bubble rather than stretch it.
                                            ...(message.role === 'assistant' && {
                                                overflowX: 'auto',
                                                '& table': { width: '100%', borderCollapse: 'collapse', fontSize: 13, my: 1 },
                                                '& th, & td': { border: '0.5px solid rgba(21,101,192,0.15)', padding: '6px 10px', textAlign: 'left' },
                                                '& th': { background: 'rgba(21,101,192,0.06)', color: '#0d47a1', fontWeight: 500 },
                                            }),
                                        }}
                                    >
                                        {message.role === 'assistant' && message.isStreaming && !message.content ? (
                                            <TypingIndicator showStatus status={agentStatus} />
                                        ) : (
                                            <>
                                                {renderText(message.content)}
                                                {/* Show streaming cursor for assistant messages being streamed */}
                                                {message.role === 'assistant' && message.isStreaming && (
                                                    <span style={{ marginLeft: 4, opacity: 0.5 }}>▊</span>
                                                )}
                                            </>
                                        )}
                                    </Paper>
                                </Box>
                                {message.role === 'assistant' && !message.isStreaming && (
                                    <MessageActionsRow
                                        message={message}
                                        references={<Citations documents={message.documents} />}
                                        onRegenerate={() => handleRegenerate(index)}
                                        onFeedback={(vote) => handleFeedback(index, message, vote)}
                                        disabled={isLoading}
                                    />
                                )}
                                {message.role === 'assistant' && !message.isStreaming && index < messages.length - 1 && (
                                    <Divider sx={{ mt: 1.5, opacity: 0.5 }} />
                                )}
                            </Box>
                            )
                        ))}

                        {/* Typing indicator when waiting for first token */}
                        {isLoading && !messages.some(m => m.isStreaming) && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
                                <Paper elevation={1} sx={{ p: 1.5, borderRadius: '12px 12px 12px 4px' }}>
                                    <TypingIndicator showStatus status={agentStatus} />
                                </Paper>
                            </Box>
                        )}

                        <div ref={messagesEndRef} />
                    </Paper>

                    {/* Input Field */}
                    <Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <ChatSettingsButton
                                provider={llmProvider}
                                onChange={setLlmProvider}
                                disabled={isLoading}
                            />
                            <TextField
                                fullWidth
                                multiline
                                maxRows={3}
                                placeholder={intl.formatMessage({ id: 'askPrompta.type_placeholder', defaultMessage: 'Type your question here...' })}
                                value={userInput}
                                onChange={(e) => setUserInput(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={!isConnected || isLoading}
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        fontSize: '0.875rem',
                                        minHeight: 48,
                                    },
                                }}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleSend}
                                disabled={!userInput.trim() || isLoading}
                                sx={{
                                    width: 32,
                                    height: 32,
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    '&:disabled': { bgcolor: 'action.disabledBackground' },
                                }}
                            >
                                <SendIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </Box>
                        <Typography sx={{ fontSize: 10.5, color: 'text.disabled', mt: 0.5, ml: 0.5 }}>
                            <FormattedMessage id="chatbot.input_hint" defaultMessage="Press ↵ to send · Shift+↵ for newline" />
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* History Drawer */}
            <Drawer
                anchor="right"
                open={historyDrawerOpen}
                onClose={() => setHistoryDrawerOpen(false)}
                disableScrollLock
                ModalProps={{
                    keepMounted: true,
                }}
                sx={{
                    zIndex: 1300,
                    '& .MuiDrawer-paper': {
                        width: { xs: '100%', sm: 360 },
                        maxWidth: '100%',
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                            <HistoryIcon />
                            <FormattedMessage id="askPrompta.chat_history" defaultMessage="Chat History" />
                        </Typography>
                        <IconButton onClick={() => setHistoryDrawerOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {loadingHistory ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : chatSessions.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                <FormattedMessage id="askPrompta.no_chat_history" defaultMessage="No chat history available" />
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ px: 0 }}>
                            {chatSessions.map((session, index) => (
                                <React.Fragment key={session.session_id}>
                                    <ListItem disablePadding>
                                        <ListItemButton
                                            onClick={() => loadSessionMessages(session.session_id)}
                                            sx={{
                                                borderRadius: 1,
                                                mb: 1,
                                                border: '1px solid',
                                                borderColor: selectedSessionId === session.session_id ? 'primary.main' : 'divider',
                                                bgcolor: selectedSessionId === session.session_id ? 'primary.50' : 'transparent',
                                                '&:hover': { bgcolor: 'action.hover' },
                                            }}
                                        >
                                            <Box sx={{ width: '100%' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                                    <ChatIcon sx={{ fontSize: 18, mt: 0.5, color: 'primary.main' }} />
                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontWeight: 'medium',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                lineHeight: 1.4,
                                                            }}
                                                        >
                                                            {session.first_question}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ display: 'block', mt: 0.5 }}
                                                        >
                                                            {formatDate(session.date)}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </ListItemButton>
                                    </ListItem>
                                    {index < chatSessions.length - 1 && <Divider sx={{ my: 1 }} />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </Box>
            </Drawer>
        </>
    );
};

export default Chatbot;
